// src/pages/sis/Subjects.js
import React, { useState, useMemo } from "react";
import { mockSubjects as initialSubjects } from "../../data/mockSubjects";
import SubjectFormModal from "../../components/SubjectFormModal";

export default function Subjects() {
  const [subjects, setSubjects] = useState(() => {
    try {
      const raw = localStorage.getItem("subjects");
      return raw ? JSON.parse(raw) : initialSubjects;
    } catch {
      return initialSubjects;
    }
  });
  const [query, setQuery] = useState("");
  const [gradeFilter, setGradeFilter] = useState("");
  const [strandFilter, setStrandFilter] = useState("");
  const [editSubject, setEditSubject] = useState(null);

  function gradeSortKey(g) {
    const s = String(g || "").toLowerCase().trim();
    if (s.startsWith("nursery")) return -2;
    if (s.includes("pre-kinder")) return -1;
    if (s.startsWith("kinder")) return 0;
    const m = s.match(/grade\s+(\d{1,2})/);
    if (m) return Number(m[1]);
    return 999; // unknown grades last
  }

  // Filters
  const grades = useMemo(() => {
    const setG = new Set(subjects.map((s) => s.gradeLevel));
    return [...setG].sort((a,b) => gradeSortKey(a) - gradeSortKey(b));
  }, [subjects]);

  const strands = useMemo(() => {
    const setS = new Set(subjects.map((s) => s.strand).filter(Boolean));
    return [...setS];
  }, [subjects]);

  // Filtered list
  const visible = subjects.filter((s) => {
    const q = query.trim().toLowerCase();
    if (q && !`${s.code} ${s.name}`.toLowerCase().includes(q)) return false;
    if (gradeFilter && s.gradeLevel !== gradeFilter) return false;
    if (strandFilter && s.strand !== strandFilter) return false;
    return true;
  }).sort((a,b) => {
    const ga = gradeSortKey(a.gradeLevel), gb = gradeSortKey(b.gradeLevel);
    if (ga !== gb) return ga - gb;
    const na = String(a.name || ""), nb = String(b.name || "");
    if (na && nb) return na.localeCompare(nb);
    return String(a.code || "").localeCompare(String(b.code || ""));
  });

  // Save handler
  const handleSave = (data) => {
    if (data.id) {
      setSubjects((prev) => {
        const next = prev.map((s) => (s.id === data.id ? data : s));
        localStorage.setItem("subjects", JSON.stringify(next));
        return next;
      });
    } else {
      const newId = `SUBJ-${String(subjects.length + 1).padStart(3, "0")}`;
      setSubjects((prev) => {
        const next = [...prev, { ...data, id: newId }];
        localStorage.setItem("subjects", JSON.stringify(next));
        return next;
      });
    }
    setEditSubject(null);
    alert("✅ Subject saved successfully!");
  };

  // Delete handler
  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this subject?")) {
      setSubjects((prev) => {
        const next = prev.filter((s) => s.id !== id);
        localStorage.setItem("subjects", JSON.stringify(next));
        return next;
      });
    }
  };

  function parseCSV(text) {
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (lines.length === 0) return [];
    const header = lines[0].split(",").map(h => h.trim());
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(",").map(c => c.trim());
      if (cols.length === 1 && cols[0] === "") continue;
      const obj = {};
      header.forEach((h, idx) => { obj[h] = cols[idx] || ""; });
      rows.push(obj);
    }
    return rows;
  }

  function handleUploadFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result || "");
        let items = [];
        if (file.name.toLowerCase().endsWith(".json")) {
          const parsed = JSON.parse(text);
          items = Array.isArray(parsed) ? parsed : [];
        } else {
          items = parseCSV(text);
        }
        const normalized = items.map((r, idx) => ({
          id: r.id || `SUBJ-${Date.now()}-${idx}`,
          code: r.code || r.Code || "",
          name: r.name || r.Name || "",
          gradeLevel: r.gradeLevel || r["gradeLevel"] || r["Grade Level"] || "",
          strand: r.strand || r.Strand || "",
          teacher: r.teacher || r.Teacher || "",
          schedule: r.schedule || r.Schedule || "",
          room: r.room || r.Room || "",
        })).filter(it => it.code && it.name);
        if (normalized.length === 0) {
          alert("No valid subjects found in file.");
          return;
        }
        setSubjects(prev => {
          const codes = new Set(prev.map(p => p.code));
          const merged = [...prev];
          normalized.forEach(n => {
            if (!codes.has(n.code)) merged.push(n);
          });
          localStorage.setItem("subjects", JSON.stringify(merged));
          return merged;
        });
        alert(`✅ Imported ${normalized.length} subject(s).`);
      } catch (err) {
        console.warn(err);
        alert("Failed to import file. Ensure it is a CSV or JSON with proper fields.");
      } finally {
        e.target.value = "";
      }
    };
    reader.readAsText(file);
  }

  // Preset generator: expands ranges like "Grades 7-10" into per-grade subjects
  const presetRows = [
    ["Grade 1","Reading and Literacy"],
    ["Grade 1","Language"],
    ["Grade 1","Mathematics"],
    ["Grade 1","Makabansa"],
    ["Grade 1","GMRC"],
    ["Grade 2","Filipino"],
    ["Grade 2","English"],
    ["Grade 2","Mathematics"],
    ["Grade 2","Makabansa"],
    ["Grade 2","GMRC"],
    ["Grade 3","Filipino"],
    ["Grade 3","English"],
    ["Grade 3","Mathematics"],
    ["Grade 3","Makabansa"],
    ["Grade 3","GMRC"],
    ["Grade 3","Science"],
    ["Grades 4-6","Filipino"],
    ["Grades 4-6","English"],
    ["Grades 4-6","Mathematics"],
    ["Grades 4-6","Science"],
    ["Grades 4-6","Araling Panlipunan"],
    ["Grades 4-6","Technology and Livelihood Education"],
    ["Grades 4-6","MAPEH"],
    ["Grades 4-6","GMRC"],
    ["Grades 7-10","Filipino"],
    ["Grades 7-10","English"],
    ["Grades 7-10","Mathematics"],
    ["Grades 7-10","Science"],
    ["Grades 7-10","Araling Panlipunan"],
    ["Grades 7-10","Technology and Livelihood Education"],
    ["Grades 7-10","MAPEH"],
    ["Grades 7-10","GMRC"],
  ];
  function acronym(name) {
    const words = String(name).split(/\s+/).filter(Boolean);
    const stop = new Set(["and","&","of","the","sa","ng"]);
    const pick = words.filter(w => !stop.has(w.toLowerCase()));
    if (pick.length === 1) return pick[0].toUpperCase().slice(0,6);
    return pick.map(w => w[0].toUpperCase()).join("").slice(0,6);
  }
  function expandPreset() {
    const out = [];
    presetRows.forEach(([grade, subj]) => {
      const make = (g) => {
        const num = Number((g.match(/\d+/)||[""])[0]);
        const name = (g.startsWith("Grade ") && /\d+/.test(g)) ? `${subj} ${num}` : subj;
        const code = `${acronym(subj)}${Number.isFinite(num)?num:""}`;
        out.push({
          id: `SUBJ-${code}-${g.replace(/\s+/g,"")}`,
          code,
          name,
          gradeLevel: g,
          strand: "",
          teacher: "",
          schedule: "",
          room: "",
        });
      };
      const m = grade.match(/^Grades?\s+(\d+)\s*-\s*(\d+)$/i);
      if (m) {
        const a = Number(m[1]), b = Number(m[2]);
        for (let n = a; n <= b; n++) make(`Grade ${n}`);
      } else {
        make(grade);
      }
    });
    setSubjects(prev => {
      const codes = new Set(prev.map(p => p.code));
      const merged = [...prev];
      out.forEach(n => { if (!codes.has(n.code)) merged.push(n); });
      localStorage.setItem("subjects", JSON.stringify(merged));
      return merged;
    });
    alert("Standard subjects added by grade level.");
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Subjects</h4>
        <div className="d-flex gap-2">
          <input type="file" accept=".csv,application/json" onChange={handleUploadFile} className="form-control form-control-sm" />
          <button className="btn btn-sm btn-outline-secondary" onClick={expandPreset}>Add Standard Subjects</button>
          <button
            className="btn btn-sm btn-primary"
            onClick={() =>
              setEditSubject({
                id: "",
                code: "",
                name: "",
                gradeLevel: "",
                strand: "",
                teacher: "",
                schedule: "",
                room: "",
              })
            }
          >
            Add Subject
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-md-4 mb-2">
          <input
            type="text"
            className="form-control"
            placeholder="Search by name or code..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="col-md-3 mb-2">
          <select
            className="form-select"
            value={gradeFilter}
            onChange={(e) => setGradeFilter(e.target.value)}
          >
            <option value="">All Grades</option>
            {grades.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 mb-2">
          <select
            className="form-select"
            value={strandFilter}
            onChange={(e) => setStrandFilter(e.target.value)}
          >
            <option value="">All Strands</option>
            {strands.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th>Code</th>
              <th>Subject Name</th>
              <th>Grade / Strand</th>
              <th>Teacher</th>
              <th>Schedule</th>
              <th>Room</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((s) => (
              <tr key={s.id}>
                <td>{s.code}</td>
                <td>{s.name}</td>
                <td>
                  {s.gradeLevel}
                  {s.strand ? ` (${s.strand})` : ""}
                </td>
                <td>{s.teacher}</td>
                <td>{s.schedule}</td>
                <td>{s.room}</td>
                <td>
                  <button
                    className="btn btn-sm btn-outline-secondary me-2"
                    onClick={() => setEditSubject(s)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDelete(s.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center">
                  No subjects found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {editSubject && (
        <SubjectFormModal
          subject={editSubject}
          onClose={() => setEditSubject(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
